import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type { OrgWithUsage, CreateInvite, InviteResult, TeamResult, UpdateOrg, OrgMember, Role } from '@seencast/shared';

// --- Per-member scopes (S7 team-scopes contract) --------------------------------
// Defined locally rather than in @seencast/shared: the backend lands the same
// shapes as real zod schemas alongside the API routes, and this file only needs
// the wire shape to talk to MSW today. Swap these for the shared exports once
// that ships — the field names below are the agreed contract, not a guess.
//
// scopes: null = org-wide (always true for owner/admin, regardless of any
// stored rows — scopes only ever bind editor/viewer). A non-null array limits
// WRITE access to those screens/groups (their own screens/schedules); reads and
// the shared media/playlist library stay org-wide for everyone.
export type ScopeType = 'screen' | 'group';

export interface MemberScope {
  scope_type: ScopeType;
  scope_id: string;
  name: string;
}

export type MemberScopes = MemberScope[] | null;

export interface ScopeRef {
  scope_type: ScopeType;
  scope_id: string;
}

export interface ScopedOrgMember extends OrgMember {
  scopes: MemberScopes;
}

export interface ScopedTeamResult extends Omit<TeamResult, 'members'> {
  members: ScopedOrgMember[];
}

export interface CreateInviteWithScopes extends CreateInvite {
  scopes?: ScopeRef[];
}

/** Owners/admins always have full org-wide access — no scope editing affordance. */
export function roleAlwaysOrgWide(role: Role): boolean {
  return role === 'owner' || role === 'admin';
}

export function getOrg(): Promise<OrgWithUsage> {
  return request<OrgWithUsage>('/org');
}

export function useOrg() {
  return useQuery({ queryKey: qk.org, queryFn: getOrg });
}

// GAP-6: team listing (see contract.ts).
export function getTeam(): Promise<ScopedTeamResult> {
  return request<ScopedTeamResult>('/org/members');
}

export function useTeam() {
  return useQuery({ queryKey: qk.team, queryFn: getTeam });
}

/**
 * Whether the signed-in member has a limited (non-org-wide) scope. Owners/admins
 * are never scoped by role; an editor/viewer is scoped only once an admin has
 * saved a scope set. Reads the roster the app already loads and finds the `is_you`
 * row. `undefined` while the roster is still loading (callers treat that as
 * "not yet known", not "scoped") — the server stays the source of truth on write.
 */
export function useIsScopedMember(): boolean | undefined {
  const team = useTeam();
  if (!team.data) return undefined;
  const me = team.data.members.find((m) => m.is_you);
  if (!me || roleAlwaysOrgWide(me.role)) return false;
  return me.scopes !== null;
}

export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInviteWithScopes) =>
      request<InviteResult>('/org/invites', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.team }),
  });
}

export interface UpdateMemberScopesVars {
  userId: string;
  /** Sent to the server as-is. null clears all limits back to org-wide. */
  scopes: ScopeRef[] | null;
  /** Pre-enriched with display names, for the optimistic team-list update. */
  optimisticScopes: MemberScopes;
}

/** PUT /v1/org/members/:userId/scopes (admin+, 403 otherwise). Optimistically
 *  updates the cached team list so the roster chip reflects the change before
 *  the round-trip completes, and rolls back on error. */
export function useUpdateMemberScopes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, scopes }: UpdateMemberScopesVars) =>
      request<ScopedOrgMember>(`/org/members/${userId}/scopes`, { method: 'PUT', body: { scopes } }),
    onMutate: async ({ userId, optimisticScopes }: UpdateMemberScopesVars) => {
      await qc.cancelQueries({ queryKey: qk.team });
      const previous = qc.getQueryData<ScopedTeamResult>(qk.team);
      if (previous) {
        qc.setQueryData<ScopedTeamResult>(qk.team, {
          ...previous,
          members: previous.members.map((m) =>
            m.user_id === userId ? { ...m, scopes: optimisticScopes } : m,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.team, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.team }),
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    // UpdateOrg = { name?; default_overlay_id?; poll_interval_s? } — default_overlay_id is
    // docs/09 §C2 (org-wide default overlay); poll_interval_s is Sprint 11 T3 (docs/04
    // §Traffic, the default manifest poll cadence, null clears back to the built-in default).
    mutationFn: (body: UpdateOrg) => request<OrgWithUsage>('/org', { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}

export function orgDefaultOverlayId(org: OrgWithUsage | undefined): string | null {
  return org?.default_overlay_id ?? null;
}
